/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/useClaimEditorController.ts
 * FILE_ID: QUOTE-CLAIMS-G10-EDITOR-HOOK-001
 * PURPOSE: Own claim editor queries, drafts, validation, autosave and conflicts.
 * WHY: The page frame must only assemble hooks, guards and presentational leaves.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  canSendClaimToXero,
  saveClaimLineUpdates,
  type ClaimLineUpdate,
  type ClaimValuationMode,
} from '@quotech/api/g10ProgressClaims'
import { useApi } from '@/lib/setupApi'
import type { PaymentClaimDetail, PaymentClaimLine } from './types'

type SaveState = 'saved' | 'pending' | 'saving' | 'offline' | 'error' | 'conflict'
type EditorView = 'valuation' | 'assessment' | 'evidence'

function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status
}

function initialUpdate(line: PaymentClaimLine): ClaimLineUpdate {
  const extended = line as PaymentClaimLine & {
    valuation_method?: ClaimValuationMode
    this_claim_quantity?: string
  }
  return {
    id: line.id,
    valuation_mode: extended.valuation_method ?? 'percentage',
    this_claim_percent: Number(line.this_claim_percent),
    this_claim_quantity: Number(extended.this_claim_quantity ?? 0),
  }
}

export function useClaimEditorController(claimId?: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState<Record<string, ClaimLineUpdate>>({})
  const [saveState, setSaveState] = useState<SaveState>(
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'saved',
  )
  const [search, setSearch] = useState('')
  const [section, setSection] = useState<'all' | 'contract' | 'variation'>('all')
  const [view, setView] = useState<EditorView>(() => {
    if (typeof window === 'undefined') return 'valuation'
    const requested = new URLSearchParams(window.location.search).get('workspace')
    return requested === 'assessment' || requested === 'evidence'
      ? requested
      : 'valuation'
  })

  const claimQuery = useQuery<PaymentClaimDetail>({
    queryKey: ['claim', claimId],
    queryFn: async () => (await api.get(`/api/claims/${claimId}/`)).data,
    enabled: Boolean(claimId),
    retry: (failureCount, error) =>
      errorStatus(error) !== 403 && failureCount < 2,
  })
  const claim = claimQuery.data

  const saveMutation = useMutation({
    mutationFn: (updates: ClaimLineUpdate[]) =>
      saveClaimLineUpdates(api, claimId as string, claim?.row_version ?? 0, updates),
    onSuccess: async () => {
      setDrafts({})
      setSaveState('saved')
      await queryClient.invalidateQueries({ queryKey: ['claim', claimId] })
    },
    onError: (error) => setSaveState(errorStatus(error) === 409 ? 'conflict' : 'error'),
  })

  const updateLine = useCallback((
    line: PaymentClaimLine,
    patch: Partial<ClaimLineUpdate>,
  ) => {
    setDrafts((current) => ({
      ...current,
      [line.id]: { ...(current[line.id] ?? initialUpdate(line)), ...patch, id: line.id },
    }))
    setSaveState(navigator.onLine ? 'pending' : 'offline')
  }, [])

  const save = useCallback(async () => {
    const updates = Object.values(drafts)
    if (!updates.length || !claimId || !claim) return
    if (!navigator.onLine) { setSaveState('offline'); return }
    setSaveState('saving')
    try {
      await saveMutation.mutateAsync(updates)
    } catch {
      // onError owns the visible error/conflict state; callers must not create
      // an unhandled rejection from background autosave.
    }
  }, [claim, claimId, drafts, saveMutation])

  useEffect(() => {
    if (saveState !== 'pending') return
    const timer = window.setTimeout(() => void save(), 1200)
    return () => window.clearTimeout(timer)
  }, [save, saveState])

  useEffect(() => {
    const online = () => { setSaveState(Object.keys(drafts).length ? 'pending' : 'saved') }
    const offline = () => setSaveState('offline')
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
  }, [drafts])

  const hasUnsavedChanges = Object.keys(drafts).length > 0
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return
      event.preventDefault()
      event.returnValue = 'Claim changes are not saved yet.'
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsavedChanges])

  const lineValue = useCallback((line: PaymentClaimLine) => drafts[line.id] ?? initialUpdate(line), [drafts])
  const visibleLines = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (claim?.lines ?? []).filter((line) => {
      const sectionMatch = section === 'all' || line.line_type === section
      const searchMatch = !term || `${line.location} ${line.description}`.toLowerCase().includes(term)
      return sectionMatch && searchMatch
    })
  }, [claim?.lines, search, section])

  const validation = useMemo(() => Object.values(drafts).flatMap((update) => {
    const line = claim?.lines.find((candidate) => candidate.id === update.id)
    if (!line) return []
    const errors: string[] = []
    const previous = Number(line.previous_percent)
    if (update.valuation_mode === 'percentage' && (update.this_claim_percent ?? 0) + previous > 100) {
      errors.push(`${line.description}: total valuation exceeds 100%.`)
    }
    if ((update.this_claim_amount ?? 0) < 0 || (update.this_claim_quantity ?? 0) < 0) {
      errors.push(`${line.description}: values cannot be negative.`)
    }
    const remaining = Number(line.value) - Number(line.previous_amount)
    if ((update.this_claim_amount ?? 0) > remaining) {
      errors.push(`${line.description}: amount exceeds the remaining contract value.`)
    }
    return errors
  }), [claim?.lines, drafts])

  const bulkPercent = useCallback((percent: number) => {
    for (const line of visibleLines.filter(
      (candidate) => initialUpdate(candidate).valuation_mode === 'percentage',
    )) updateLine(line, {
      valuation_mode: 'percentage',
      this_claim_percent: Math.max(0, Math.min(percent, 100 - Number(line.previous_percent))),
    })
  }, [updateLine, visibleLines])
  const copyPrior = useCallback(() => {
    for (const line of visibleLines.filter(
      (candidate) => initialUpdate(candidate).valuation_mode === 'percentage',
    )) updateLine(line, {
      valuation_mode: 'percentage',
      this_claim_percent: Math.max(0, Math.min(Number(line.previous_percent), 100 - Number(line.previous_percent))),
    })
  }, [updateLine, visibleLines])
  const clearCurrent = useCallback(() => {
    for (const line of visibleLines) updateLine(line, {
      valuation_mode: 'percentage',
      this_claim_percent: 0,
      this_claim_amount: 0,
      this_claim_quantity: 0,
    })
  }, [updateLine, visibleLines])

  const actionMutation = useMutation({
    mutationFn: async (action: 'submit' | 'approve' | 'send_to_xero') =>
      (await api.post(`/api/claims/${claimId}/${action}/`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['claim', claimId] }),
  })

  return {
    claim, api, isLoading: claimQuery.isLoading, error: claimQuery.error,
    isPermissionDenied: errorStatus(claimQuery.error) === 403,
    retry: claimQuery.refetch, drafts, lineValue, visibleLines, updateLine,
    search, setSearch, section, setSection, view, setView, save, saveState,
    hasUnsavedChanges, validation, bulkPercent, copyPrior, clearCurrent,
    canSendXero: claim ? canSendClaimToXero(claim) : false,
    runAction: actionMutation.mutate, actionPending: actionMutation.isPending,
  }
}

export type ClaimEditorController = ReturnType<typeof useClaimEditorController>
